"use client";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { signUp } from "@/lib/auth-client";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { passwordSchema, PASSWORD_HINT } from "@/lib/password-schema";

const RegisterSchema = z
  .object({
    firstName: z.string().trim().min(1, "First name is required").max(60),
    lastName: z.string().trim().min(1, "Last name is required").max(60),
    email: z.string().email(),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type RegisterForm = z.infer<typeof RegisterSchema>;

export default function RegisterPage() {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);
  const [verifySent, setVerifySent] = useState(false);

  const form = useForm<RegisterForm>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    const { error } = await signUp.email({
      email: values.email,
      password: values.password,
      name: `${values.firstName} ${values.lastName}`.trim(),
      firstName: values.firstName,
      lastName: values.lastName,
    });
    if (error) {
      setServerError(error.message ?? "Sign-up failed");
      return;
    }
    setVerifySent(true);
    setTimeout(() => router.push("/verify-email"), 1500);
  });

  if (verifySent) {
    return (
      <Card>
        <h2
          className="text-[22px] font-semibold mb-2"
          style={{ letterSpacing: "-0.02em" }}
        >
          Check your email
        </h2>
        <p className="text-[14px] text-[var(--color-on-surface-variant)]">
          We sent a verification link to{" "}
          <strong>{form.getValues("email")}</strong>. Click the link to confirm
          your account and sign in.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-7">
        <h1
          className="text-[24px] font-semibold text-[var(--color-on-surface)]"
          style={{ letterSpacing: "-0.025em" }}
        >
          Create your account
        </h1>
        <p className="text-[13.5px] text-[var(--color-on-surface-variant)] mt-1.5">
          We&apos;ll set up your default workspace automatically.
        </p>
      </div>

      <form onSubmit={onSubmit} className="stagger space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First name"
            {...form.register("firstName")}
            error={form.formState.errors.firstName?.message}
            autoComplete="given-name"
          />
          <Input
            label="Last name"
            {...form.register("lastName")}
            error={form.formState.errors.lastName?.message}
            autoComplete="family-name"
          />
        </div>
        <Input
          label="Email"
          type="email"
          {...form.register("email")}
          error={form.formState.errors.email?.message}
          autoComplete="email"
        />
        <Input
          label="Password"
          type="password"
          {...form.register("password")}
          error={form.formState.errors.password?.message}
          autoComplete="new-password"
          hint={PASSWORD_HINT}
          showPasswordToggle={true}
        />
        <Input
          label="Confirm password"
          type="password"
          {...form.register("confirmPassword")}
          error={form.formState.errors.confirmPassword?.message}
          autoComplete="new-password"
          showPasswordToggle={true}
        />
        {serverError && (
          <p className="text-[13px] text-[var(--color-error)]">{serverError}</p>
        )}
        <Button
          type="submit"
          loading={form.formState.isSubmitting}
          className="w-full"
          size="lg"
        >
          Create account
        </Button>
      </form>

      <p className="text-[13px] text-center mt-7 text-[var(--color-on-surface-variant)]">
        Already have an account?{" "}
        <Link href="/login" className="font-medium text-[var(--color-primary)]">
          Sign in
        </Link>
      </p>
    </Card>
  );
}
